/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable tailwindcss/no-arbitrary-value */
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { useProductBlockAsideModal } from '@hyphengroup/cms-components/components/ProductBlockAsideModal';
import RewardAsideModal from '@hyphengroup/cms-components/components/RewardAsideModal/RewardAsideModal';
import useOpenRewardModal from '@hyphengroup/cms-components/hooks/useOpenRewardModal';
import { filterOffersByAmountAndTenure } from '@hyphengroup/cms-components/utils/helpers/offers';
import classNames from 'classnames';
import { useLocaleSpecificClient } from 'hooks/useSpecificClient';
import { Fragment } from 'react';
import { staticText } from 'utils/staticText';
import { LoanSearchParams } from '../../helpers/loanSearchParams';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../helpers/sendAnalytics';
import { pushDataLayer } from '../../pushDataLayer';
import { PageType, VerticalType } from '../../types';
import { getColorSchema } from './helpers/getColorSchema';
import { getFirstOrSecond } from './helpers/getFirstOrSecond';
import {
  AttrNumber,
  CmsProduct,
  PromotionCampaign,
} from '$/(cms)/_modules/cmsService/types';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';

const translations = staticText({
  cards: {
    _: 'Participating Products:',
    'zh-hk': '指定金融產品:',
    'zh-tw': '適用產品：',
  },
});

export const PromotionParticipatingCards = (props: {
  activeCampaign: PromotionCampaign;
  products: { label?: string; product?: CmsProduct | null; id?: string }[];
  vertical?: VerticalType;
  pageType: PageType;
  loanParams: LoanSearchParams;
  blockIndex?: number;
  activePromotionIndex?: number;
  /** Page slug, reported as `item_list_name` of the product click. */
  slug?: string;
}) => {
  const cardsText = useLocaleSpecificClient(translations.cards);
  const { openModal } = useOpenRewardModal();
  const { closeModal: closeProductAside } = useProductBlockAsideModal();
  const { amount, tenure } = props.loanParams;
  const colorSchema = getColorSchema(props.activeCampaign.color ?? 'Blue');
  const rewardModalComponentKey = `Campaign_${props.blockIndex}`;
  // Tracking reports the position in the rendered list, so skip the empty
  // relations before indexing.
  const products = props.products.filter((p) => !!p.product);
  // "_pdp" keeps the key distinct from the per-product RewardAsideModal below,
  // which shares the same rewardModal hash (CMS-1924).
  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `CampaignCards_${props.blockIndex}_pdp`,
  });

  return (
    <>
      <h4 className="mhg-alpha-typography-bullet-1">{cardsText} </h4>
      <div
        data-component="promotion-participating-cards"
        className="my-spacing/gap/soft-12px flex flex-col gap-spacing/gap/soft-12px"
      >
        {products.map((p, index) => {
          const prod = p.product!;

          const whyApplyAttr = p.product?.attributes?.find(
            (a) => a.name?.name === 'WHY_APPLY_NOW',
          ) as AttrNumber;

          const label = getFirstOrSecond(
            p.label,
            [
              whyApplyAttr?.label ?? '',
              whyApplyAttr?.empty_value_text ?? '',
            ].join(' '),
          );
          const welcomeOffers = filterOffersByAmountAndTenure(
            prod.welcome_offers ?? [],
            amount,
            tenure,
          );
          const exclusiveOffers = filterOffersByAmountAndTenure(
            [
              ...(prod.exclusive_offers ?? []),
              ...(prod.bundle_exclusive_offers ?? []),
            ],
            amount,
            tenure,
          );

          return (
            <Fragment key={prod.title}>
              <div
                data-product-id={prod.uid}
                onClick={() => {
                  // Fired on the click itself, not on modal open, so the event
                  // survives a modal that fails to render (CMS-1686 #2241).
                  sendProductAnalytics({
                    event: 'product_interaction',
                    action: UserActionEnum.CLICK,
                    componentType: ComponentTypeEnum.TEXT_LINK,
                    componentTitle: `Offer Card/Product List_${prod.product_name ?? ''}`,
                    url: props.slug,
                    product: prod,
                    index: index.toString(),
                  });
                  if (
                    props.vertical === 'PERSONAL_LOANS' &&
                    (prod.welcome_offers?.length ||
                      prod.exclusive_offers?.length ||
                      prod.bundle_exclusive_offers?.length)
                  ) {
                    openModal(prod.uid ?? '', rewardModalComponentKey);
                    return;
                  }

                  if (prod.url_alias) {
                    openProductDetail(prod, null);
                  } else {
                    closeProductAside();
                  }
                }}
                className="flex cursor-pointer flex-row items-center gap-spacing/gap/soft-8px"
              >
                <CmsImage
                  src={prod?.product_image ?? ''}
                  alt={prod.product_name!}
                  height={28}
                  width={44}
                  sizes={{ _: 44 }}
                  className="!h-7 !w-11 object-contain"
                  style={{ objectFit: 'contain' }}
                  loading="eager"
                />
                <div className="flex flex-col lg:flex-row lg:items-center">
                  <h5
                    className={classNames(
                      'mhg-alpha-typography-bullet-2 md:mhg-alpha-typography-bullet-1',
                      colorSchema.cardText,
                    )}
                  >
                    {prod.product_name}
                  </h5>
                  {label ? <h5 className="hidden lg:block">:&nbsp;</h5> : null}
                  {label ? (
                    <h5 className="mhg-alpha-typography-bullet-2 md:mhg-alpha-typography-bullet-1">
                      {' '}
                      {label}
                    </h5>
                  ) : null}
                </div>
              </div>
              {/* Sibling of the clickable row, not a child: the modal backdrop
                  click must not bubble into the row's tracking handler. */}
              <RewardAsideModal
                welcomeOffers={welcomeOffers}
                exclusiveOffers={exclusiveOffers}
                loanSearchParams={props.loanParams}
                product={prod}
                keyFromComponent={rewardModalComponentKey}
                pushDataLayer={pushDataLayer}
                ctaLinkOverride={undefined}
                vertical={props.vertical}
                carInsuranceCtaComponent={null}
                priceLine={null}
              />
            </Fragment>
          );
        })}
      </div>
      {productDetailModal}
    </>
  );
};
